import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Building2,
  CalendarRange,
  Minus,
  RefreshCw,
  Wallet,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Alert } from '../components/ui/SimpleAlert';
import { DashboardLoanSimulator } from '../components/DashboardLoanSimulator';
import { apiService } from '../services/api.service';

type CompareMode = 'none' | 'previous' | 'mom' | 'yoy';
type PeriodPreset =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last30'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'custom';

const PERIOD_OPTIONS: Array<{ value: PeriodPreset; label: string }> = [
  { value: 'today', label: 'Hoy' },
  { value: 'yesterday', label: 'Ayer' },
  { value: 'last7', label: 'Últimos 7 días' },
  { value: 'last30', label: 'Últimos 30 días' },
  { value: 'thisMonth', label: 'Este mes' },
  { value: 'lastMonth', label: 'Mes anterior' },
  { value: 'thisYear', label: 'Este año' },
  { value: 'custom', label: 'Personalizado' },
];

const COMPARE_OPTIONS: Array<{ value: CompareMode; label: string }> = [
  { value: 'previous', label: 'Período anterior' },
  { value: 'mom', label: 'Mismo período mes ant.' },
  { value: 'yoy', label: 'Mismo período año ant.' },
  { value: 'none', label: 'Sin comparación' },
];

function WidgetSkeleton({ className = 'h-28' }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-xl bg-slate-100 border border-slate-200 ${className}`} />
  );
}

function money(n: number | null | undefined) {
  if (n == null) return '—';
  return `$ ${n.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function pct(n: number | null | undefined) {
  if (n == null) return '—';
  return `${n.toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`;
}

function DeltaBadge({
  delta,
  kind,
  compareLabel,
  format = 'money',
}: {
  delta: { absolute: number; percent: number | null; direction: 'up' | 'down' | 'flat' } | null;
  kind: 'positive_up' | 'negative_up';
  compareLabel?: string;
  format?: 'money' | 'count';
}) {
  if (!delta) return null;
  const tone =
    delta.direction === 'flat'
      ? 'neutral'
      : kind === 'positive_up'
        ? delta.direction === 'up'
          ? 'good'
          : 'bad'
        : delta.direction === 'up'
          ? 'bad'
          : 'good';
  const color =
    tone === 'good' ? 'text-emerald-700' : tone === 'bad' ? 'text-rose-700' : 'text-slate-500';
  const Icon =
    delta.direction === 'up' ? ArrowUpRight : delta.direction === 'down' ? ArrowDownRight : Minus;
  const absoluteLabel =
    format === 'count'
      ? delta.absolute.toLocaleString('es-AR')
      : money(delta.absolute);
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${color}`}>
      <Icon className="h-3.5 w-3.5" />
      {delta.percent == null ? absoluteLabel : pct(Math.abs(delta.percent))}
      {compareLabel ? <span className="text-slate-400 font-normal ml-1">vs {compareLabel}</span> : null}
    </span>
  );
}

function KpiCard({
  label,
  value,
  subtitle,
  delta,
  kind,
  compareLabel,
  deltaFormat = 'money',
  href,
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  subtitle?: React.ReactNode;
  delta?: { absolute: number; percent: number | null; direction: 'up' | 'down' | 'flat' } | null;
  kind?: 'positive_up' | 'negative_up';
  compareLabel?: string;
  deltaFormat?: 'money' | 'count';
  href?: string;
  onClick?: () => void;
}) {
  const clickable = Boolean(href || onClick);
  const body = (
    <Card
      className={`p-4 h-full border-slate-200/80 shadow-sm ${clickable ? 'hover:border-slate-400 transition-colors' : ''}`}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-slate-900 tabular-nums">{value}</p>
      {subtitle ? <div className="mt-1 text-sm text-slate-600">{subtitle}</div> : null}
      {delta && kind ? (
        <div className="mt-2">
          <DeltaBadge
            delta={delta}
            kind={kind}
            compareLabel={compareLabel}
            format={deltaFormat}
          />
        </div>
      ) : null}
    </Card>
  );
  if (href) return <Link to={href}>{body}</Link>;
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="text-left w-full cursor-pointer">
        {body}
      </button>
    );
  }
  return body;
}

function Section({
  title,
  action,
  children,
  className = '',
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={`p-5 border-slate-200/80 shadow-sm ${className}`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-slate-500 py-8 text-center">{message}</p>;
}

function GoalRow({
  label,
  actual,
  target,
  percent,
  format = 'money',
}: {
  label: string;
  actual: number;
  target: number;
  percent: number | null;
  format?: 'money' | 'number';
}) {
  if (target <= 0) {
    return (
      <div className="py-2">
        <div className="flex justify-between text-sm">
          <span className="text-slate-700">{label}</span>
          <span className="text-slate-500">Sin objetivo configurado</span>
        </div>
        <p className="text-xs text-slate-400 mt-0.5">
          Actual: {format === 'money' ? money(actual) : actual}
        </p>
      </div>
    );
  }
  const width = Math.min(100, percent ?? 0);
  const bar =
    (percent ?? 0) >= 100 ? 'bg-emerald-500' : (percent ?? 0) >= 70 ? 'bg-sky-600' : 'bg-amber-500';
  return (
    <div className="py-2">
      <div className="flex justify-between text-sm">
        <span className="text-slate-700">{label}</span>
        <span className="font-medium text-slate-900 tabular-nums">
          {pct(percent)} · {format === 'money' ? money(actual) : actual} /{' '}
          {format === 'money' ? money(target) : target}
        </span>
      </div>
      <div className="mt-1 h-2 w-full rounded-full bg-slate-100">
        <div className={`h-2 rounded-full ${bar}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<PeriodPreset>('today');
  const [compare, setCompare] = useState<CompareMode>('previous');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState<Array<{ id: string; name: string }>>([]);
  const [placementMode, setPlacementMode] = useState<'amount' | 'count'>('amount');
  const [sortKey, setSortKey] = useState<'disbursedAmount' | 'collectedAmount' | 'delinquencyPercent'>(
    'disbursedAmount',
  );
  const [data, setData] = useState<any | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isLoading = loadingSummary || loadingCharts || loadingDetail;

  const compareLabel =
    data?.comparison?.label?.replace('Período anterior', 'ant.') ??
    (compare === 'previous' ? 'ant.' : compare === 'mom' ? 'mes ant.' : compare === 'yoy' ? 'año ant.' : undefined);

  const buildParams = (sections: string) => {
    const params: Record<string, string | number | undefined> = {
      branchId: branchId || undefined,
      compare,
      sections,
    };
    if (preset === 'custom') {
      params.preset = 'custom';
      params.from = customFrom;
      params.to = customTo;
    } else {
      params.preset = preset;
    }
    return params;
  };

  const mergeDash = (partial: any) => {
    setData((prev: any) => ({
      ...(prev ?? {}),
      ...partial,
      series: partial.series ?? prev?.series,
      collectionsSummary: partial.collectionsSummary ?? prev?.collectionsSummary,
      cashFlow: partial.cashFlow ?? prev?.cashFlow,
      kpis: partial.kpis ?? prev?.kpis,
      today: partial.today ?? prev?.today,
      branches: partial.branches ?? prev?.branches,
      goals: partial.goals ?? prev?.goals,
      delinquencyAging: partial.delinquencyAging ?? prev?.delinquencyAging,
      origination: partial.origination ?? prev?.origination,
      openCashSessions: partial.openCashSessions ?? prev?.openCashSessions,
      alerts: partial.alerts ?? prev?.alerts,
    }));
  };

  const load = async () => {
    setError(null);
    setLoadingSummary(true);
    setLoadingCharts(true);
    setLoadingDetail(true);
    setData(null);

    try {
      const brsPromise = apiService.getBranches(true).then((brs) => {
        setBranches(brs);
        if (!branchId && brs.length === 1) setBranchId(brs[0].id);
      });

      // 1) KPIs + hoy — pinta rápido
      const summaryPromise = apiService
        .getOperationalDashboard(buildParams('summary'))
        .then((dash) => {
          mergeDash(dash);
          setLoadingSummary(false);
        })
        .catch((err) => {
          setLoadingSummary(false);
          throw err;
        });

      // 2) Charts + ranking en paralelo tras arrancar summary (no esperan entre sí)
      const chartsPromise = apiService
        .getOperationalDashboard(buildParams('charts'))
        .then((dash) => {
          mergeDash(dash);
          setLoadingCharts(false);
        })
        .catch(() => setLoadingCharts(false));

      const detailPromise = apiService
        .getOperationalDashboard(buildParams('detail'))
        .then((dash) => {
          mergeDash(dash);
          setLoadingDetail(false);
        })
        .catch(() => setLoadingDetail(false));

      await Promise.all([brsPromise, summaryPromise]);
      await Promise.all([chartsPromise, detailPromise]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar el tablero operativo');
      setLoadingSummary(false);
      setLoadingCharts(false);
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (preset === 'custom' && (!customFrom || !customTo)) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, compare, branchId, customFrom, customTo]);

  const sortedBranches = useMemo(() => {
    const rows = [...(data?.branches ?? [])];
    rows.sort((a: any, b: any) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));
    return rows;
  }, [data?.branches, sortKey]);

  const placementChartData = useMemo(() => {
    const current = data?.series?.placements ?? [];
    const compareSeries = data?.series?.placementsCompare;
    if (!compareSeries?.length) {
      return current.map((p: any) => ({
        date: p.date.slice(5),
        actual: placementMode === 'amount' ? p.amount : p.count,
        amount: p.amount,
        count: p.count,
        avgTicket: p.avgTicket,
      }));
    }
    return current.map((p: any, i: number) => {
      const c = compareSeries[i];
      return {
        date: p.date.slice(5),
        actual: placementMode === 'amount' ? p.amount : p.count,
        compare: c ? (placementMode === 'amount' ? c.amount : c.count) : 0,
        amount: p.amount,
        count: p.count,
        avgTicket: p.avgTicket,
      };
    });
  }, [data?.series, placementMode]);

  if (error && !data) {
    return (
      <div className="p-6">
        <Alert variant="error" title="Error">
          {error}
          <button onClick={load} className="ml-3 text-sm underline">
            Reintentar
          </button>
        </Alert>
      </div>
    );
  }

  const k = data?.kpis;
  const today = data?.today;
  const goals = data?.goals;
  const hasPlacement = (data?.series?.placements ?? []).some((p: any) => p.amount > 0 || p.count > 0);
  const hasCollection = (data?.series?.collections ?? []).some((p: any) => p.amount > 0 || p.expected > 0);
  const hasCashFlow = (data?.series?.cashFlow ?? []).some((p: any) => p.inflows > 0 || p.outflows > 0);
  const agingTotal = (data?.delinquencyAging ?? []).reduce((s: number, b: any) => s + b.amount, 0);

  return (
    <div className="min-h-full bg-gradient-to-b from-slate-50 to-white">
      <div className="p-4 md:p-6 space-y-5 max-w-[1600px] mx-auto">
        {/* Header + filters */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
              Tablero operativo
            </h1>
            <p className="mt-1 text-sm text-slate-500 flex flex-wrap items-center gap-2">
              <CalendarRange className="h-4 w-4" />
              {data?.period?.label ?? 'Cargando…'}
              {data?.period ? ` · ${data?.period.from} → ${data?.period.to}` : ''}
              <span className="text-slate-300">·</span>
              {data?.timezone?.replace('America/Argentina/', '') ?? 'Buenos_Aires'}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Flujo (colocado / cobranza / altas) = período de arriba · Cartera / mora = stock actual
            </p>
          </div>

          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                Sucursal
              </label>
              <select
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm min-w-[160px]"
                disabled={branches.length === 1}
              >
                {branches.length !== 1 && <option value="">Toda la empresa</option>}
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                Período
              </label>
              <select
                value={preset}
                onChange={(e) => setPreset(e.target.value as PeriodPreset)}
                className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
              >
                {PERIOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {preset === 'custom' && (
              <>
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                    Desde
                  </label>
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                    Hasta
                  </label>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                Comparar con
              </label>
              <select
                value={compare}
                onChange={(e) => setCompare(e.target.value as CompareMode)}
                className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
              >
                {COMPARE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={load}
              disabled={isLoading}
              className="h-9 inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
          </div>
        </div>

        {error && (
          <Alert variant="warning" title="Advertencia">
            {error}
          </Alert>
        )}

        {/* Fila 1 — KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3">
          {loadingSummary || !k ? (
            Array.from({ length: 6 }).map((_, i) => <WidgetSkeleton key={i} />)
          ) : (
            <>
          <KpiCard
            label="Colocado en el período"
            value={money(k.disbursedAmount.value)}
            subtitle={`${k.disbursedAmount.count.toLocaleString('es-AR')} altas · ticket ${money(k.disbursedAmount.avgTicket)}`}
            delta={k.disbursedAmount.delta}
            kind="positive_up"
            compareLabel={compareLabel}
          />
          <KpiCard
            label="Cobranza del período"
            value={money(k.collectedAmount.value)}
            subtitle={`${k.collectedAmount.count.toLocaleString('es-AR')} cobros (toda la cartera)`}
            delta={k.collectedAmount.delta}
            kind="positive_up"
            compareLabel={compareLabel}
            href="/payments"
          />
          <KpiCard
            label="Altas del período"
            value={k.loansGranted.value.toLocaleString('es-AR')}
            subtitle="Desembolsos nuevos · no es el total de la base"
            delta={k.loansGranted.delta}
            kind="positive_up"
            compareLabel={compareLabel}
            deltaFormat="count"
          />
          <KpiCard
            label="Mora (stock)"
            value={money(k?.delinquency?.overdueAmount)}
            subtitle={`${pct(k?.delinquency?.percent)} de cartera viva · ${k?.delinquency?.overdueCount?.toLocaleString('es-AR') ?? 0} cuotas`}
            kind="negative_up"
            href="/collections"
          />
          <KpiCard
            label="Cartera viva (stock)"
            value={money(k?.portfolio?.outstanding)}
            subtitle={`${(k?.portfolio?.activeLoans ?? 0).toLocaleString('es-AR')} préstamos activos · no incluye cancelados/cerrados`}
            href="/loans"
          />
          <KpiCard
            label="Caja disponible"
            value={money(k.cash.balanceTotal)}
            subtitle={`${k.cash.openSessions} cajas abiertas`}
            href="/cash"
          />
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
          <div className="lg:col-span-8 space-y-5">
        {/* Fila 2 — Colocaciones + Hoy */}
        <div className="grid grid-cols-1 xl:grid-cols-10 gap-4">
          <Section
            className="xl:col-span-7"
            title="Colocaciones del período"
            action={
              <div className="flex gap-1 rounded-md border border-slate-200 p-0.5 text-xs">
                <button
                  className={`px-2 py-1 rounded ${placementMode === 'amount' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
                  onClick={() => setPlacementMode('amount')}
                >
                  $
                </button>
                <button
                  className={`px-2 py-1 rounded ${placementMode === 'count' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
                  onClick={() => setPlacementMode('count')}
                >
                  Cant.
                </button>
              </div>
            }
          >
            {loadingCharts ? (
              <WidgetSkeleton className="h-64" />
            ) : !hasPlacement ? (
              <EmptyState message="Sin movimientos para este período" />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={placementChartData}>
                    <defs>
                      <linearGradient id="placeFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0f172a" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#0f172a" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      tickFormatter={(v) =>
                        placementMode === 'amount'
                          ? `${(v / 1_000_000).toFixed(v >= 1_000_000 ? 1 : 2)}M`
                          : String(v)
                      }
                    />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                      formatter={(value: number, name) => [
                        placementMode === 'amount' ? money(Number(value)) : value,
                        name === 'actual' ? 'Actual' : 'Comparación',
                      ]}
                      labelFormatter={(label) => String(label)}
                    />
                    <Area
                      type="monotone"
                      dataKey="actual"
                      stroke="#0f172a"
                      fill="url(#placeFill)"
                      strokeWidth={2}
                      name="actual"
                    />
                    {data?.series?.placementsCompare ? (
                      <Area
                        type="monotone"
                        dataKey="compare"
                        stroke="#94a3b8"
                        fill="transparent"
                        strokeDasharray="4 4"
                        strokeWidth={1.5}
                        name="compare"
                      />
                    ) : null}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          <Section
            className="xl:col-span-3"
            title="Hoy"
            action={<span className="text-xs text-slate-400">vs ayer</span>}
          >
            <div className="space-y-3 text-sm">
              {[
                {
                  label: 'Prestado',
                  value: money(today?.disbursedAmount),
                  prev: money(today?.compare?.disbursedAmount),
                },
                {
                  label: 'Préstamos desembolsados',
                  value: today?.loansGranted,
                  prev: today?.compare?.loansGranted,
                },
                {
                  label: 'Cobrado',
                  value: money(today?.collectedAmount),
                  prev: money(today?.compare?.collectedAmount),
                },
                {
                  label: 'Cobros',
                  value: today?.paymentsCount,
                  prev: today?.compare?.paymentsCount,
                },
                { label: 'Cuotas vence hoy', value: today?.installmentsDueToday },
                { label: 'Cuotas en mora hoy', value: today?.installmentsOverdueToday },
                {
                  label: 'Presolicitudes nuevas',
                  value: today?.applicationsNew,
                  prev: today?.compare?.applicationsNew,
                },
                { label: 'Presolic. aprobadas / rechazadas', value: `${today?.applicationsApproved} / ${today?.applicationsRejected}` },
                { label: 'Renovaciones', value: today?.renewals },
                { label: 'Clientes nuevos', value: today?.customersNew },
                {
                  label: 'Cajas abiertas',
                  value: today?.openCashSessions,
                  href: '/cash',
                },
              ].map((row) => (
                <div key={row.label} className="flex items-baseline justify-between gap-2 border-b border-slate-100 pb-2 last:border-0">
                  <span className="text-slate-500">{row.label}</span>
                  <div className="text-right">
                    {row.href ? (
                      <Link to={row.href} className="font-semibold text-slate-900 hover:underline tabular-nums">
                        {row.value}
                      </Link>
                    ) : (
                      <span className="font-semibold text-slate-900 tabular-nums">{row.value}</span>
                    )}
                    {'prev' in row && row.prev != null ? (
                      <p className="text-[11px] text-slate-400">ayer {row.prev}</p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* Fila 3 — Cobranza + Mora */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section
            title="Evolución de cobranza"
            action={
              <span className="text-xs text-slate-500">
                Cumplimiento{' '}
                <strong className="text-slate-800">
                  {pct(data?.collectionsSummary?.fulfillmentPercent)}
                </strong>
              </span>
            }
          >
            <div className="mb-3 grid grid-cols-3 gap-2 text-center text-sm">
              <div className="rounded-lg bg-slate-50 p-2">
                <p className="text-[11px] text-slate-500">Cobrado</p>
                <p className="font-semibold tabular-nums">{money(data?.collectionsSummary?.collected)}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-2">
                <p className="text-[11px] text-slate-500">Esperado</p>
                <p className="font-semibold tabular-nums">{money(data?.collectionsSummary?.expected)}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-2">
                <p className="text-[11px] text-slate-500">Cumplimiento</p>
                <p className="font-semibold tabular-nums">
                  {pct(data?.collectionsSummary?.fulfillmentPercent)}
                </p>
              </div>
            </div>
            {!hasCollection ? (
              <EmptyState message="Sin movimientos para este período" />
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={(data?.series?.collections ?? []).map((c: any) => ({ ...c, date: c.date.slice(5) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
                    <Tooltip
                      formatter={(v: number, name: string) => [
                        money(v),
                        name === 'amount' ? 'Cobrado' : 'Esperado',
                      ]}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="expected" stroke="#94a3b8" fill="#f1f5f9" name="expected" />
                    <Area type="monotone" dataKey="amount" stroke="#0369a1" fill="#e0f2fe" name="amount" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          <Section title="Evolución de mora" action={<Link to="/collections" className="text-sm text-sky-700 hover:underline">Cobranzas</Link>}>
            <div className="mb-4 flex items-end justify-between">
              <div>
                <p className="text-3xl font-semibold text-slate-900 tabular-nums">{pct(k?.delinquency?.percent)}</p>
                <p className="text-sm text-slate-500">de cartera · {money(k?.delinquency?.overdueAmount)}</p>
              </div>
              <p className="text-sm text-slate-500">{k?.delinquency?.overdueCount} cuotas</p>
            </div>
            {agingTotal <= 0 ? (
              <EmptyState message="Sin mora para el corte actual" />
            ) : (
              <div className="space-y-3">
                {(data?.delinquencyAging ?? []).map((b: any) => (
                  <div key={b.key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">{b.label}</span>
                      <span className="tabular-nums text-slate-800">
                        {money(b.amount)} · {b.count} · {pct(b.percent)}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full bg-rose-500/80"
                        style={{ width: `${Math.min(100, b.percent)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* Fila 4 — Sucursales + Objetivos */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
          <Section
            className="xl:col-span-3"
            title="Rendimiento por sucursal"
            action={
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as any)}
                className="h-8 rounded border border-slate-200 text-xs px-2"
              >
                <option value="disbursedAmount">Orden: colocado</option>
                <option value="collectedAmount">Orden: cobrado</option>
                <option value="delinquencyPercent">Orden: mora</option>
              </select>
            }
          >
            {sortedBranches.length === 0 ? (
              <EmptyState message="Sin sucursales en alcance" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                      <th className="py-2 pr-2 font-medium">Sucursal</th>
                      <th className="py-2 pr-2 font-medium text-right">Colocado</th>
                      <th className="py-2 pr-2 font-medium text-right">Cobrado</th>
                      <th className="py-2 pr-2 font-medium text-right">Mora</th>
                      <th className="py-2 pr-2 font-medium text-right">Cartera</th>
                      <th className="py-2 font-medium text-right">Objetivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBranches.map((b: any) => (
                      <tr
                        key={b.branchId}
                        className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setBranchId(b.branchId)}
                      >
                        <td className="py-2.5 pr-2">
                          <span className="font-medium text-slate-900 inline-flex items-center gap-1.5">
                            <Building2 className="h-3.5 w-3.5 text-slate-400" />
                            {b.branchName}
                          </span>
                          <p className="text-[11px] text-slate-400">{b.loansCount} préstamos · ticket {money(b.avgTicket)}</p>
                        </td>
                        <td className="py-2.5 pr-2 text-right tabular-nums">{money(b.disbursedAmount)}</td>
                        <td className="py-2.5 pr-2 text-right tabular-nums">{money(b.collectedAmount)}</td>
                        <td className={`py-2.5 pr-2 text-right tabular-nums ${b.delinquencyPercent >= 10 ? 'text-rose-700 font-medium' : ''}`}>
                          {pct(b.delinquencyPercent)}
                        </td>
                        <td className="py-2.5 pr-2 text-right tabular-nums">{money(b.portfolioOutstanding)}</td>
                        <td className="py-2.5 text-right tabular-nums">
                          {b.hasGoal ? pct(b.goalSalesPercent) : <span className="text-slate-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section
            className="xl:col-span-2"
            title={`Objetivos ${goals?.month ?? '—'}/${goals?.year ?? '—'}`}
            action={<Link to="/objectives" className="text-sm text-sky-700 hover:underline">Detalle</Link>}
          >
            {loadingDetail ? (
              <WidgetSkeleton className="h-40" />
            ) : !goals?.configured ? (
              <EmptyState message="Sin objetivos configurados para este mes" />
            ) : (
              <>
                <GoalRow
                  label="Monto colocado"
                  actual={goals?.salesAmount.actual}
                  target={goals?.salesAmount.target}
                  percent={goals?.salesAmount.percent}
                />
                <GoalRow
                  label="Cantidad préstamos"
                  actual={goals?.salesCount.actual}
                  target={goals?.salesCount.target}
                  percent={goals?.salesCount.percent}
                  format="number"
                />
                <GoalRow
                  label="Cobranza"
                  actual={goals?.collection.actual}
                  target={goals?.collection.target}
                  percent={goals?.collection.percent}
                />
                {goals?.projectedClosePercent != null && (
                  <p className="mt-3 text-xs text-slate-500">
                    Al ritmo actual: <strong className="text-slate-800">{pct(goals?.projectedClosePercent)}</strong> estimado al cierre
                    {goals?.remainingDays > 0 ? ` · ${goals?.remainingDays} días restantes` : ''}
                  </p>
                )}
              </>
            )}
          </Section>
        </div>

        {/* Fila 5 — Flujo + Originación */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Flujo de fondos (caja)">
            <div className="mb-3 grid grid-cols-3 gap-2 text-center text-sm">
              <div className="rounded-lg bg-emerald-50/80 p-2">
                <p className="text-[11px] text-emerald-700">Ingresos</p>
                <p className="font-semibold text-emerald-900 tabular-nums">{money(data?.cashFlow?.inflows)}</p>
              </div>
              <div className="rounded-lg bg-rose-50/80 p-2">
                <p className="text-[11px] text-rose-700">Egresos</p>
                <p className="font-semibold text-rose-900 tabular-nums">{money(data?.cashFlow?.outflows)}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-2">
                <p className="text-[11px] text-slate-600">Neto</p>
                <p className="font-semibold text-slate-900 tabular-nums">{money(data?.cashFlow?.net)}</p>
              </div>
            </div>
            {!hasCashFlow ? (
              <EmptyState message="Sin movimientos de caja en el período" />
            ) : (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(data?.series?.cashFlow ?? []).map((c: any) => ({ ...c, date: c.date.slice(5) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
                    <Tooltip formatter={(v: number) => money(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend />
                    <Bar dataKey="inflows" name="Ingresos" fill="#059669" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="outflows" name="Egresos" fill="#e11d48" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <p className="mt-2 text-[11px] text-slate-400">
              Basado en movimientos de caja ({data?.cashFlow?.paymentCount} cobros). No incluye bancos.
            </p>
          </Section>

          <Section title="Originación" action={<Link to="/applications" className="text-sm text-sky-700 hover:underline">Presolicitudes</Link>}>
            <p className="text-[11px] text-slate-400 mb-2">
              Stock de presolicitudes abiertas. Distinto de «Préstamos otorgados» (créditos ya desembolsados).
            </p>
            <div className="space-y-3">
              {[
                { label: 'Pendientes', value: data?.origination?.pending, href: '/applications' },
                { label: 'En análisis', value: data?.origination?.underReview, href: '/applications' },
                { label: 'Aprobadas', value: data?.origination?.approved },
                { label: 'Rechazadas', value: data?.origination?.rejected },
                { label: 'Desembolsadas / convertidas', value: data?.origination?.converted },
              ].map((row) => {
                const funnelTotal =
                  data?.origination?.pending +
                  data?.origination?.underReview +
                  data?.origination?.approved +
                  data?.origination?.rejected +
                  data?.origination?.converted;
                return (
                <div key={row.label} className="flex items-center gap-3">
                  <div className="w-28 text-sm text-slate-600">{row.label}</div>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-2 bg-slate-800"
                      style={{
                        width: `${Math.max(4, (row.value / Math.max(1, funnelTotal)) * 100)}%`,
                      }}
                    />
                  </div>
                  {row.href ? (
                    <Link to={row.href} className="w-10 text-right font-semibold tabular-nums text-slate-900 hover:underline">
                      {row.value}
                    </Link>
                  ) : (
                    <span className="w-10 text-right font-semibold tabular-nums">{row.value}</span>
                  )}
                </div>
              );
              })}
              <p className="text-sm text-slate-500 pt-2">
                Conversión a desembolso:{' '}
                <strong className="text-slate-800">{pct(data?.origination?.conversionPercent)}</strong>
              </p>
            </div>
          </Section>
        </div>

        {/* Fila 6 — Cartera / alertas / cajas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Section title="Cartera viva (stock actual)">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-500">Saldo pendiente</dt>
                <dd className="font-semibold tabular-nums">{money(k?.portfolio?.outstanding)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Intereses pendientes (est.)</dt>
                <dd className="font-semibold tabular-nums">{money(k?.portfolio?.interestOutstanding)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Préstamos activos</dt>
                <dd className="font-semibold tabular-nums">
                  {(k?.portfolio?.activeLoans ?? 0).toLocaleString('es-AR')}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Ticket promedio origen</dt>
                <dd className="font-semibold tabular-nums">{money(k?.portfolio?.avgTicket)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Plazo promedio</dt>
                <dd className="font-semibold tabular-nums">{k?.portfolio?.avgTermMonths} meses</dd>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-3">
                <dt className="text-slate-500">Ticket del período (flujo)</dt>
                <dd className="font-semibold tabular-nums flex flex-col items-end">
                  {money(k?.avgTicket?.value)}
                  <DeltaBadge delta={k?.avgTicket?.delta} kind="positive_up" compareLabel={compareLabel} />
                </dd>
              </div>
              <p className="text-xs text-slate-400 pt-1">
                El listado de préstamos (~70 mil) incluye cerrados/cancelados; acá solo activos.
              </p>
            </dl>
          </Section>

          <Section
            title="Requiere atención"
            action={<AlertTriangle className="h-4 w-4 text-amber-600" />}
          >
            {((data?.alerts ?? []).length === 0) ? (
              <EmptyState message="Sin alertas relevantes" />
            ) : (
              <ul className="space-y-2">
                {(data?.alerts ?? []).map((a: any) => (
                  <li key={a.id}>
                    {a.href ? (
                      <button
                        type="button"
                        onClick={() => navigate(a.href)}
                        className="w-full text-left rounded-lg border border-slate-100 p-3 hover:bg-slate-50"
                      >
                        <AlertRow alert={a} />
                      </button>
                    ) : (
                      <div className="rounded-lg border border-slate-100 p-3">
                        <AlertRow alert={a} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section
            title="Estado de cajas"
            action={
              <Link to="/cash" className="text-sm text-sky-700 hover:underline inline-flex items-center gap-1">
                <Wallet className="h-3.5 w-3.5" /> Operaciones
              </Link>
            }
          >
            {(data?.openCashSessions ?? []).length === 0 ? (
              <EmptyState message="No hay sesiones de caja abiertas" />
            ) : (
              <ul className="space-y-2">
                {(data?.openCashSessions ?? []).map((s: any) => (
                  <li key={s.sessionId} className="rounded-lg bg-slate-50 p-3 text-sm">
                    <div className="flex justify-between gap-2">
                      <div>
                        <p className="font-medium text-slate-900">
                          {s.branchName} · Caja {s.cashRegisterNumber}
                        </p>
                        <p className="text-xs text-slate-500">{s.operatorName}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold tabular-nums">{money(s.balance)}</p>
                        <p className="text-[11px] text-slate-400">{s.hoursOpen}h abierta</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
          </div>
          <aside className="lg:col-span-4 lg:sticky lg:top-4">
            <DashboardLoanSimulator branchId={branchId} branches={branches} />
          </aside>
        </div>
      </div>
    </div>
  );
};

function AlertRow({ alert }: { alert: any }) {
  const color =
    alert.severity === 'high'
      ? 'bg-rose-100 text-rose-800'
      : alert.severity === 'medium'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-slate-100 text-slate-700';
  return (
    <>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${color}`}>
          {alert.severity}
        </span>
        <span className="font-medium text-slate-900 text-sm">{alert.title}</span>
      </div>
      <p className="text-xs text-slate-500">{alert.detail}</p>
    </>
  );
}
